import { describeApiError, type RoomMembership } from '@brush/api-client';
import { ErrorNotice, ErrorRetry, Spinner } from '@brush/ui';
import { Link2, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { contactChangeNote, methodEvidence } from '../../memberships';
import { useContactCoverage, useRevokeMembership, useRoomMemberships } from '../../queries';

const DAY = 24 * 60 * 60 * 1000;

function when(iso: string) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Who has connected a sign-in to one of this room's cards, and how that was proved
 * (ADR 0010, Decision 7).
 *
 * The panel exists because the audit columns were being written and read by nothing. A membership
 * grants somebody the right to read every finish, prize and standing a card has been paid against,
 * and Decision 8 removes the operator link path that would otherwise be where a room exercised
 * judgement about that. What stands in its place is this screen: the method, the evidence, a
 * contact changed shortly beforehand, and how often somebody else has asked for the card. None of
 * it refuses anything, and that is the design — a control that works by being read.
 */
export function MembershipsPanel({ roomId }: { roomId: string }) {
  const membershipsQ = useRoomMemberships(roomId);
  const coverageQ = useContactCoverage(roomId);
  const revoke = useRevokeMembership(roomId);

  const memberships = membershipsQ.data ?? [];

  return (
    <section className="mt-8 overflow-hidden rounded-xl border border-ink-800/10 bg-white shadow-sm">
      <header className="border-b border-ink-800/10 px-6 py-4">
        <h2 className="flex items-center gap-2 font-display font-semibold text-ink-950">
          <Link2 className="size-4 text-ink-800/60" /> Connected accounts
        </h2>
        <p className="mt-1 text-xs leading-relaxed text-ink-800/60">
          Players who have connected a sign-in to one of this room&apos;s cards, and what each of
          them proved. A player does this themselves, by reading a code sent to the contact details
          on the card — nobody at the desk can connect a card on their behalf, so correcting those
          details is the only lever this room has here.
        </p>
      </header>

      <div className="p-6">
        <ErrorNotice message={describeApiError(revoke.error)} className="mb-4" />

        {membershipsQ.isLoading ? (
          <Spinner label="Loading connected accounts…" />
        ) : membershipsQ.isError ? (
          <ErrorRetry
            message="Couldn't load connected accounts."
            onRetry={() => void membershipsQ.refetch()}
          />
        ) : memberships.length === 0 ? (
          <Empty reachable={coverageQ.data?.reachable} total={coverageQ.data?.total} />
        ) : (
          <>
            <ul className="space-y-3">
              {memberships.map((m) => (
                <MembershipRow
                  key={m.playerId}
                  membership={m}
                  pending={revoke.isPending}
                  onRevoke={() => revoke.mutate(m.playerId)}
                />
              ))}
            </ul>

            {/* The window, said once for the panel rather than on every row. A card with no
                attempts shows no line at all, so without this sentence an absence would read as
                "nobody has ever tried" — which no row survives long enough to support. */}
            <p className="mt-4 text-[11px] leading-relaxed text-ink-800/40">
              Refused attempts are kept for {windowDays(memberships)} days. A card with none listed
              has had none in that time, which is not the same as never.
            </p>
          </>
        )}
      </div>
    </section>
  );
}

/**
 * The one line saying why a room is looking at nothing — and it is nearly always the unpopulated
 * reason rather than a fault.
 *
 * Split on contact coverage because the two cases send an operator to different places: with no
 * addresses on file nobody *can* connect, and the work is on the screen above this one; with
 * addresses on file this is simply a room whose players have not got round to it.
 */
function Empty({ reachable, total }: { reachable?: number; total?: number }) {
  if (total === 0) {
    return (
      <p className="py-6 text-center text-sm text-ink-800/50">
        No players registered yet, so there is nothing for anyone to connect to.
      </p>
    );
  }

  return (
    <p className="py-6 text-center text-sm text-ink-800/50">
      {reachable === 0
        ? 'Nobody has connected an account yet, and nobody can until a card has an email address on it — add one above.'
        : 'Nobody has connected an account to one of this room’s cards yet.'}
    </p>
  );
}

function MembershipRow({
  membership,
  pending,
  onRevoke,
}: {
  membership: RoomMembership;
  pending: boolean;
  onRevoke: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const { label, gloss } = methodEvidence(membership.method);
  const changeNote = contactChangeNote(membership);

  return (
    <li className="rounded-lg border border-ink-800/10 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2 text-sm">
            <span className="font-mono text-xs text-ink-800/70">{membership.cardId}</span>
            <span className="font-medium text-ink-950">{membership.playerName}</span>
          </div>
          <div className="mt-0.5 text-xs text-ink-800/60">
            held by{' '}
            {/* The sign-in's own name, and it is routinely a different spelling of the same
                person. A card held by an account named nothing like it is exactly what an operator
                would want to notice, so the two are shown together rather than one standing in for
                the other. */}
            <span className="text-ink-950">
              {membership.accountDisplayName ?? 'an account with no name on it'}
            </span>{' '}
            · connected {when(membership.linkedAt)}
          </div>
        </div>

        {!confirming ? (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="shrink-0 rounded-lg border border-chip-500/40 px-3 py-1.5 text-xs text-chip-500 transition hover:bg-chip-500/5"
          >
            Revoke
          </button>
        ) : null}
      </div>

      {/* Shown, never behind a tooltip. It is the difference between "proved control of the
          mailbox we hold" and "the room's own directory vouched" — the fact the audit exists to
          carry, and the one an operator asked to defend a link needs in front of them. */}
      <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="rounded border border-ink-800/15 bg-felt-50/50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-800/70">
          {label}
        </span>
        <span className="text-xs text-ink-800/60">{gloss}</span>
      </div>

      {membership.evidenceRef ? (
        <p className="mt-1.5 break-all font-mono text-[10px] text-ink-800/40">
          {membership.evidenceRef}
        </p>
      ) : null}

      {changeNote ? (
        <p className="mt-1.5 text-xs text-ink-950">
          {changeNote}{' '}
          <span className="text-ink-800/50">
            Ordinary if the desk was correcting it while they waited.
          </span>
        </p>
      ) : null}

      {membership.refusedClaimCount > 0 && membership.lastRefusedClaimAt ? (
        <p className="mt-1.5 flex items-start gap-1.5 text-xs text-ink-800/70">
          <ShieldAlert className="mt-0.5 size-3.5 shrink-0 text-ink-800/40" />
          <span>
            {membership.refusedClaimCount === 1
              ? 'Someone else has tried to connect this card once since it was claimed'
              : `Someone else has tried to connect this card ${membership.refusedClaimCount} times since it was claimed`}
            , most recently {when(membership.lastRefusedClaimAt)}. Each was refused and no code was
            sent.{' '}
            {/* Who is deliberately absent. The point of holding an unproven claimant's identity on
                a row that expires is that it stays there — and under Decision 8 there is nothing
                an operator could do with a name anyway. What they can act on is that the card is
                contested. */}
            <span className="text-ink-800/50">We don’t record who.</span>
          </span>
        </p>
      ) : null}

      {confirming ? (
        <div className="mt-3 rounded-lg border border-chip-500/30 bg-chip-500/5 p-3">
          <p className="text-xs leading-relaxed text-ink-950">
            Disconnect {membership.accountDisplayName ?? 'this account'} from card{' '}
            <span className="font-mono">{membership.cardId}</span>? They lose access to it in their
            portal. The card itself, and every result, standing and prize it has been paid against,
            stay exactly as they are — and it can be connected again with a new code.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={onRevoke}
              disabled={pending}
              className="rounded-lg bg-chip-500 px-3 py-1.5 text-xs font-medium text-white transition hover:opacity-90 disabled:opacity-50"
            >
              {pending ? 'Revoking…' : 'Revoke access'}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={pending}
              className="rounded-lg border border-ink-800/15 px-3 py-1.5 text-xs text-ink-800/70 transition hover:bg-ink-800/5 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

/**
 * How far back the refused-attempt figures can see, taken from the server's own floor rather than
 * from a constant here. The retention horizon is coupled server-side to the per-card send limit it
 * has to outlive, so a copy in the client would be a second home for a number that has to hold a
 * relationship this side cannot see.
 */
function windowDays(memberships: RoomMembership[]): number {
  const since = Date.parse(memberships[0].refusedClaimsSince);
  return Math.max(1, Math.round((Date.now() - since) / DAY));
}
